import * as React from 'react';
import type { ImpoundResponseDTO } from '@relay-towing/api';
import {
  Button,
  CallCard,
  firstLine,
  locality,
  PageHeader,
  Stack,
  StatPair,
  Text,
} from '@relay-towing/ui';
import { Invoice, PhoneCall, Plus, Wall } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useCompanyStats } from '../../hooks/useCompanyStats';
import { useMember } from '../../hooks/useMember';
import { usePagedList } from '../../hooks/usePagedList';
import { UNDER_WAY, callListParams } from '../../lib/callLists';
import { seesOfficeHome, seesTasks } from '../../lib/driverAppTabs';
import type { CallRecord } from '../calls/callRecord';
import { CreateCallSheet } from '../calls/CreateCallSheet';
import { periodSince } from '../period';
import { TabScreen } from '../shell/TabScreen';
import { NearLimitBanners } from './NearLimitBanners';
import { TasksSection } from './TasksSection';

const money = (amount: number | undefined) =>
  amount === undefined
    ? '—'
    : amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });

/**
 * The office's day: the company's calls, and its money for a role that may
 * see every invoice. Only rendered with `reports.view`; the analytics
 * endpoint refuses anyone else, so it is not asked.
 */
function OfficeDay() {
  const member = useMember();
  const range = React.useMemo(() => periodSince('day'), []);
  const { data: stats, isLoading } = useCompanyStats(range);
  return (
    <Stack gap={0} divided>
      <StatPair
        title="Calls"
        icon={PhoneCall}
        stats={[
          {
            label: 'Today',
            value: stats?.totalCalls ?? '—',
            loading: isLoading,
          },
          {
            label: 'Completed',
            value: stats?.completedCalls ?? '—',
            loading: isLoading,
          },
        ]}
      />
      {member.can('invoices.view_all') ? (
        <StatPair
          title="Invoices"
          icon={Invoice}
          stats={[
            {
              label: 'Invoiced today',
              value: money(stats?.totalInvoiced),
              loading: isLoading,
            },
            {
              label: 'Paid today',
              value: money(stats?.totalPaid),
              loading: isLoading,
            },
          ]}
        />
      ) : null}
    </Stack>
  );
}

/**
 * A driver's own day: the call they are on, or the next one assigned to
 * them, and today's numbers, with no company money. Not a list of calls:
 * that is the Calls tab's, and Home repeated it, Assigned plus today's
 * finished ones (the owner's decision of 2026-10-20). A driver has one
 * call open at a time, so one row says it. No Figma frame draws this; the
 * three "Today's activity" frames are the office's.
 *
 * Each figure is the server's answer to its own question rather than rows
 * counted here: the call is the first of the Calls tab's Assigned view,
 * and done today is their calls completed or GOA today (`view=today`).
 */
function DriverDay() {
  const navigate = useNavigate();
  const member = useMember();
  const mine = usePagedList<CallRecord>(
    'mobile-home-my-call',
    '/calls',
    callListParams('assigned'),
    { pollEvery: 30_000 }
  );
  const done = usePagedList<CallRecord>(
    'mobile-home-done-today',
    '/calls',
    {
      assignedToMe: true,
      view: 'today',
      status: ['COMPLETED', 'GOA'],
    },
    { pollEvery: 30_000 }
  );
  // The Impounds tab's keys: without one there is no impounds figure, as
  // there is no tab. It was drawn anyway, a dash with nothing behind it.
  const seesImpounds =
    member.can('impounds.view_own') || member.can('impounds.view_all');
  const impounds = usePagedList<ImpoundResponseDTO>(
    'mobile-home-impounds',
    '/impounds',
    { status: 'ACTIVE' },
    { enabled: seesImpounds }
  );
  const call = mine.items[0];
  // Under way, or assigned and waiting for them to start it.
  const started = call && UNDER_WAY.has(call.status);

  return (
    <Stack gap={4}>
      <Stack gap={0} divided>
        <StatPair
          title="Calls"
          icon={PhoneCall}
          stats={[
            {
              label: 'Done today',
              value: done.meta?.totalCount ?? '—',
              loading: done.isLoading,
            },
            {
              label: 'Assigned',
              value: mine.meta?.totalCount ?? '—',
              loading: mine.isLoading,
            },
          ]}
        />
        {seesImpounds ? (
          <StatPair
            title="Impounds"
            icon={Wall}
            stats={[
              {
                label: 'Yours at the lot',
                value: impounds.meta?.totalCount ?? '—',
                loading: impounds.isLoading,
              },
            ]}
          />
        ) : null}
      </Stack>
      {mine.isLoading ? null : call ? (
        <Stack gap={2} as="section">
          <Text variant="overline" tone="muted" as="h2">
            {started ? 'Your call' : 'Next call'}
          </Text>
          <CallCard
            platform="phone"
            address={firstLine(call.pickupLocation)}
            locality={locality(call.pickupLocation)}
            status={call.status}
            priority={call.priority}
            number={call.dispatchNumber}
            onClick={() => navigate(`/calls/${call.id}`)}
          />
        </Stack>
      ) : (
        <Text variant="body" tone="muted">
          No call assigned to you.
        </Text>
      )}
    </Stack>
  );
}

/**
 * Home, as the Figma's "Today's activity" lays it out: + Call beside the
 * heading for a role that may create one, then the day in sections ruled
 * off from each other — the office's for an office role (an office member
 * who also drives gets this one), a driver's own otherwise — then tasks, and
 * the near-limit nudge for whoever manages the plan. Inspections, drivers
 * and reports are in the Functions and Operations tabs.
 */
export function HomeScreen() {
  const member = useMember();
  const office = seesOfficeHome(member);
  const [creating, setCreating] = React.useState(false);
  // A driver-only member cannot create calls; the server refuses them too.
  const canCreate = member.can('calls.create');

  return (
    <TabScreen>
      <NearLimitBanners />
      {/* The day's first section starts right under the heading, as the
          Figma has it: its own top padding is the space between them. */}
      <Stack gap={0}>
        <PageHeader
          title="Today’s activity"
          level="section"
          layout="row"
          actions={
            canCreate ? (
              <Button
                variant="soft"
                size="lg"
                pill
                leadingIcon={<Plus />}
                onClick={() => setCreating(true)}
              >
                Call
              </Button>
            ) : undefined
          }
        />
        {office ? (
          member.can('reports.view') ? (
            <OfficeDay />
          ) : null
        ) : (
          <DriverDay />
        )}
      </Stack>
      {seesTasks(member) ? <TasksSection /> : null}
      {creating ? <CreateCallSheet open onOpenChange={setCreating} /> : null}
    </TabScreen>
  );
}
